import React, { useState } from "react";
import { usePlayer } from "@empirica/core/player/classic/react";

// Learning Experiment - 15 Topics across 5 Domains (3 per domain)
// These must match the topics in server/src/configs/
const TOPICS_BY_DOMAIN = {
  "stem": {
    name: "STEM",
    topics: [
      "gas-pressure",
      "mole-concept-molar-mass",
      "simple-harmonic-motion"
    ]
  },
  "finance": {
    name: "Finance",
    topics: [
      "bonds",
      "markets",
      "taxation-principles"
    ]
  },
  "social-sciences": {
    name: "Social Sciences",
    topics: [
      "dev-psy",
      "learning",
      "urban-geography"
    ]
  },
  "health": {
    name: "Health",
    topics: [
      "fluid-electrolytes",
      "blood-typing",
      "nursing-cna"
    ]
  },
  "work-certifications": {
    name: "Work Certifications",
    topics: [
      "electrical-fundamentals",
      "food-safety-fundamentals",
      "osha"
    ]
  }
};

const CONDITIONS = [
  { id: "ai", label: "AI Chat" },
  { id: "pdf", label: "PDF Reading" },
  // ai-pdf and web are disabled this version (removed from availableConditions);
  // re-add here if they are re-enabled in treatments.yaml.
  // { id: "ai-pdf", label: "AI Chat with PDF" },
  // { id: "web", label: "Web Search" },
];

const ROLES = [
  { id: "auto", label: "Auto-assign (based on familiarity)" },
  { id: "Teacher", label: "Teacher" },
  { id: "Student", label: "Student" }
];

function formatTopicName(topic) {
  return topic
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatDomainName(domain) {
  return TOPICS_BY_DOMAIN[domain]?.name || domain;
}

export function DevModeSelection({ next }) {
  const player = usePlayer();
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedCondition, setSelectedCondition] = useState("ai");
  const [selectedRole, setSelectedRole] = useState("auto");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedTopic) {
      alert("Please select a topic");
      return;
    }

    // Store selections in player state for server to use
    player.set("devSelection", {
      topic: selectedTopic,
      condition: selectedCondition,
      role: selectedRole === "auto" ? null : selectedRole
    });

    console.log("[DevModeSelection] Saved:", {
      topic: selectedTopic,
      condition: selectedCondition,
      role: selectedRole
    });

    next();
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
        <div className="text-center mb-6">
          <div className="inline-block bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
            DEV MODE
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Development Configuration
          </h1>
          <p className="text-gray-600 mt-2">
            Select the topic and condition you want to test
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Topic Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Topic
            </label>
            <select
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select a topic --</option>
              {Object.entries(TOPICS_BY_DOMAIN).map(([domainId, domain]) => (
                <optgroup key={domainId} label={domain.name}>
                  {domain.topics.map(topic => (
                    <option key={topic} value={topic}>
                      {formatTopicName(topic)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Condition Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Condition
            </label>
            <select
              value={selectedCondition}
              onChange={(e) => setSelectedCondition(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {CONDITIONS.map(cond => (
                <option key={cond.id} value={cond.id}>
                  {cond.label}
                </option>
              ))}
            </select>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role (optional)
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {ROLES.map(role => (
                <option key={role.id} value={role.id}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Selected Summary */}
          {selectedTopic && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Configuration Summary</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><strong>Topic:</strong> {formatTopicName(selectedTopic)}</li>
                <li><strong>Condition:</strong> {CONDITIONS.find(c => c.id === selectedCondition)?.label}</li>
                <li><strong>Role:</strong> {ROLES.find(r => r.id === selectedRole)?.label}</li>
              </ul>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Start Game
          </button>
        </form>
      </div>
    </div>
  );
}
